import { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { generateOrganizationSchema, generateWebSiteSchema } from '@/utils/seo';
import { supabase } from '@/integrations/supabase/client';

const SITE_URL = 'https://caivonclub.se';

interface SiteInfo {
  seo_logo_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  tiktok_url?: string;
  youtube_url?: string;
  show_facebook?: boolean;
  show_instagram?: boolean;
  show_tiktok?: boolean;
  show_youtube?: boolean;
}

/**
 * GlobalSEO component handles site-wide SEO elements:
 * - Organization schema JSON-LD with dynamic logo from database
 * - Language/direction attributes
 */
export const GlobalSEO = () => {
  const { language } = useLanguage();
  const [siteInfo, setSiteInfo] = useState<SiteInfo | null>(null);

  // Fetch site info for SEO logo and social links
  useEffect(() => {
    const fetchSiteInfo = async () => {
      const { data } = await supabase
        .from('site_information')
        .select('seo_logo_url, facebook_url, instagram_url, tiktok_url, youtube_url, show_facebook, show_instagram, show_tiktok, show_youtube')
        .single();
      
      if (data) {
        setSiteInfo(data);
      }
    };
    
    fetchSiteInfo();
  }, []);

  useEffect(() => {
    // Update document lang attribute
    document.documentElement.lang = language;
    
    // Set dir attribute for RTL support (Arabic)
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  // Update Organization and WebSite schemas when site info loads
  useEffect(() => {
    // Remove existing schemas
    const existingOrgSchema = document.querySelector('script[data-schema="organization"]');
    if (existingOrgSchema) {
      existingOrgSchema.remove();
    }
    const existingWebSiteSchema = document.querySelector('script[data-schema="website"]');
    if (existingWebSiteSchema) {
      existingWebSiteSchema.remove();
    }

    // Build social links array from visible social media
    const socialLinks: string[] = [];
    if (siteInfo?.show_facebook && siteInfo?.facebook_url) {
      socialLinks.push(siteInfo.facebook_url);
    }
    if (siteInfo?.show_instagram && siteInfo?.instagram_url) {
      socialLinks.push(siteInfo.instagram_url);
    }
    if (siteInfo?.show_tiktok && siteInfo?.tiktok_url) {
      socialLinks.push(siteInfo.tiktok_url);
    }
    if (siteInfo?.show_youtube && siteInfo?.youtube_url) {
      socialLinks.push(siteInfo.youtube_url);
    }

    // Add Organization schema with alternate names for brand recognition
    const orgSchemaScript = document.createElement('script');
    orgSchemaScript.type = 'application/ld+json';
    orgSchemaScript.setAttribute('data-schema', 'organization');
    orgSchemaScript.textContent = generateOrganizationSchema({
      seoLogoUrl: siteInfo?.seo_logo_url,
      socialLinks: socialLinks.length > 0 ? socialLinks : undefined,
    });
    document.head.appendChild(orgSchemaScript);

    // Add WebSite schema for brand disambiguation
    const webSiteSchemaScript = document.createElement('script');
    webSiteSchemaScript.type = 'application/ld+json';
    webSiteSchemaScript.setAttribute('data-schema', 'website');
    webSiteSchemaScript.textContent = generateWebSiteSchema();
    document.head.appendChild(webSiteSchemaScript);
  }, [siteInfo]);

  return null;
};

export default GlobalSEO;
